/* eslint-disable react-refresh/only-export-components */
import { useRef, useState, type Dispatch, type SetStateAction } from 'react';

import { toProposalQuestionViewState } from '@/adapters/answer-adapter';
import type { ProposalQuestionViewState } from '@/types/backend-contracts';
import { askProposalQuestion } from '@/services/question-service';
import { useBrowserQueryCache } from '@/lib/query-cache/use-browser-query-cache';
import { sanitizeQuestionText } from '@/utils/questionText';

export function normalizeFirebaseError(error: unknown): string {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = String((error as { code?: unknown }).code ?? '').replace(/^functions\//, '');

    if (code === 'resource-exhausted') {
      return 'O limite temporário de consultas foi atingido. Tente novamente em instantes.';
    }

    if (code === 'invalid-argument') {
      return 'A pergunta enviada é inválida.';
    }

    if (code === 'failed-precondition' || code === 'unavailable' || code === 'internal' || code === 'permission-denied' || code === 'unauthenticated') {
      return 'Não foi possível consultar as propostas agora. Tente novamente.';
    }

    if (code === 'not-found') {
      return 'A resposta compartilhada não foi encontrada.';
    }
  }

  return 'Não foi possível consultar as propostas agora. Tente novamente.';
}

export function useProposalQuestion(): {
  state: ProposalQuestionViewState;
  askQuestion: (question: string, selectedCandidate?: { id: string; name: string }) => Promise<void>;
  setState: Dispatch<SetStateAction<ProposalQuestionViewState>>;
} {
  const queryCache = useBrowserQueryCache();
  const [state, setState] = useState<ProposalQuestionViewState>({ status: 'idle' });
  const loadingRef = useRef(false);

  async function askQuestion(question: string, selectedCandidate?: { id: string; name: string }): Promise<void> {
    if (loadingRef.current) {
      return;
    }

    loadingRef.current = true;
    setState({ status: 'loading' });

    const sanitizedQuestion = sanitizeQuestionText(question);

    try {
      const response = await askProposalQuestion({
        question: sanitizedQuestion,
        selectedCandidateId: selectedCandidate?.id,
        selectedCandidateName: selectedCandidate?.name,
      }, { cache: queryCache });
      setState(toProposalQuestionViewState(response));
    } catch (error) {
      setState({
        status: 'error',
        message: normalizeFirebaseError(error),
      });
    } finally {
      loadingRef.current = false;
    }
  }

  return {
    state,
    askQuestion,
    setState,
  };
}
